/**
 * <carousel-progress> — generic carousel progress bar + prev/next controls.
 *
 * Drives a thin progress track (thumb sized to the visible portion) and a pair
 * of round buttons for any horizontally-scrolling carousel. Engine-agnostic: it
 * observes a plain scroll container, so it works with the native Horizon
 * <slideshow-component> scroller and with bespoke scroll-snap grids alike.
 *
 * Expected markup (rendered by Liquid or by a host component):
 *   <carousel-progress data-scroller="slideshow-slides">
 *     <div class="carousel-controls__progress"><span class="carousel-controls__progress-fill"></span></div>
 *     <button data-carousel-prev>…</button>
 *     <button data-carousel-next>…</button>
 *   </carousel-progress>
 *
 * The progress track is optional: a host that wants a "1 / 4" counter instead
 * renders [data-carousel-current] and [data-carousel-total] elements and no
 * track. Each part is looked up independently, so any combination works.
 *
 * Attributes:
 *   data-scroller — CSS selector for the scroll container (required to do anything).
 *   data-scope    — optional CSS selector for the ancestor to search within;
 *                   defaults to the closest <slideshow-component>, else the parent.
 *
 * If the scroller can't be resolved the element stays inert (no-op), so an
 * unconfigured instance never changes behaviour.
 */
class CarouselProgress extends HTMLElement {
  /** @type {HTMLElement | null} */
  #scroller = null;

  /** @type {HTMLElement | null} */
  #fill = null;

  /** @type {HTMLButtonElement | null} */
  #prev = null;

  /** @type {HTMLButtonElement | null} */
  #next = null;

  /** @type {HTMLElement | null} */
  #current = null;

  /** @type {HTMLElement | null} */
  #total = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /**
   * Cached width of one "page". getComputedStyle is too expensive to call on
   * every scroll event, and the value only changes when the scroller resizes.
   * @type {number | null}
   */
  #step = null;

  connectedCallback() {
    const selector = this.dataset.scroller;
    if (!selector) return;

    // Selectors come from markup (a block id can end up in data-scope), so a
    // malformed one must leave the element inert rather than throw.
    /** @type {Element | null} */
    let scroller = null;
    try {
      const scope =
        (this.dataset.scope && this.closest(this.dataset.scope)) ||
        this.closest('slideshow-component') ||
        this.parentElement ||
        document;

      scroller = scope.querySelector(selector);
    } catch {
      return;
    }

    if (!(scroller instanceof HTMLElement)) return;

    this.#scroller = scroller;
    this.#fill = this.querySelector('.carousel-controls__progress-fill');
    this.#prev = this.querySelector('[data-carousel-prev]');
    this.#next = this.querySelector('[data-carousel-next]');
    this.#current = this.querySelector('[data-carousel-current]');
    this.#total = this.querySelector('[data-carousel-total]');

    this.#prev?.addEventListener('click', () => this.#scrollByAmount(-1));
    this.#next?.addEventListener('click', () => this.#scrollByAmount(1));

    scroller.addEventListener('scroll', this.#update, { passive: true });

    // ResizeObserver fires when the scroller first gets a size (e.g. revealed
    // after a tab switch), guaranteeing correct scrollWidth/clientWidth then.
    this.#resizeObserver = new ResizeObserver(() => {
      this.#step = null;
      this.#update();
    });
    this.#resizeObserver.observe(scroller);
  }

  disconnectedCallback() {
    this.#scroller?.removeEventListener('scroll', this.#update);
    this.#resizeObserver?.disconnect();
  }

  /**
   * Width of one "page": a single item plus the gap, falling back to the
   * visible width when there is no item to measure.
   * @returns {number}
   */
  #stepSize() {
    if (this.#step != null) return this.#step;

    const scroller = this.#scroller;
    if (!scroller) return 0;

    const item = scroller.firstElementChild;
    if (!(item instanceof HTMLElement)) return scroller.clientWidth;

    const gap = parseFloat(getComputedStyle(scroller).columnGap) || 0;
    this.#step = item.offsetWidth + gap;
    return this.#step;
  }

  /**
   * Scroll by one "page" (the width of a single item including the gap).
   * @param {number} direction - -1 for previous, 1 for next.
   */
  #scrollByAmount(direction) {
    const scroller = this.#scroller;
    if (!scroller) return;

    scroller.scrollBy({ left: this.#stepSize() * direction, behavior: 'smooth' });
  }

  #update = () => {
    const scroller = this.#scroller;
    if (!scroller) return;

    const scrollMax = scroller.scrollWidth - scroller.clientWidth;

    if (this.#prev) this.#prev.disabled = scroller.scrollLeft <= 1;
    if (this.#next) this.#next.disabled = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 1;

    if (this.#fill) {
      // Thumb width = visible / total; slide it within the remaining track.
      const thumbPct = (scroller.clientWidth / scroller.scrollWidth) * 100;
      const positionPct = scrollMax > 0 ? (scroller.scrollLeft / scrollMax) * (100 - thumbPct) : 0;
      this.#fill.style.width = `${thumbPct}%`;
      this.#fill.style.left = `${positionPct}%`;
    }

    if (this.#current || this.#total) {
      const count = Array.from(scroller.children).filter(
        (child) => child instanceof HTMLElement && !child.hidden && !child.hasAttribute('data-peek-clone')
      ).length;
      const step = this.#stepSize();
      const index = step > 0 ? Math.round(scroller.scrollLeft / step) : 0;

      if (this.#total) this.#total.textContent = `${count}`;
      if (this.#current) this.#current.textContent = `${Math.min(Math.max(index + 1, 1), Math.max(count, 1))}`;
    }
  };
}

if (!customElements.get('carousel-progress')) {
  customElements.define('carousel-progress', CarouselProgress);
}
